import React from "react";
import {
    useGetGiftedUserSharesByUserIdQuery,
    useGetPurchasedUserSharesByUserIdQuery,
    useDeletePurchasedSharesMutation,
    useDeleteGiftedSharesMutation,
} from "../../api";
import { useNotification } from "../../hooks/useNotification";
import type { UserShare, UserSharesSummary } from "../../api/admin";
import { Trash2Icon } from "lucide-react";

interface UserSharesModalProps {
    sharesType: 0 | 1;
    userId: number | null;
    userData: UserSharesSummary["user"] | null;
    onClose: () => void;
    onSharesUpdate?: () => void;
}

export const UserSharesModal: React.FC<UserSharesModalProps> = ({ userId, userData, onClose, sharesType, onSharesUpdate }) => {
    const { showNotification } = useNotification();

    // Gifted Shares Query
    const {
        data: giftedSharesData,
        isLoading: isLoadingGifted,
        refetch: refetchGifted,
    } = useGetGiftedUserSharesByUserIdQuery(
        { userId: userId! },
        { skip: sharesType !== 0 || !userId }
    );

    // Purchased Shares Query
    const {
        data: purchasedSharesData,
        isLoading: isLoadingPurchased,
        refetch: refetchPurchased,
    } = useGetPurchasedUserSharesByUserIdQuery(
        { userId: userId! },
        { skip: sharesType !== 1 || !userId }
    );

    // Mutations
    const [deleteGiftedShares, { isLoading: isDeletingGifted }] = useDeleteGiftedSharesMutation();
    const [deletePurchasedShares, { isLoading: isDeletingPurchased }] = useDeletePurchasedSharesMutation();

    const isLoading = isLoadingGifted || isLoadingPurchased;
    const isDeleting = isDeletingGifted || isDeletingPurchased;

    // Determine which data to use based on sharesType
    const userSharesData: UserShare[] = (sharesType === 0 ? giftedSharesData?.data : purchasedSharesData?.data) || [];

    const handleDeleteShare = async (shareId: number) => {
        if (window.confirm("Вы уверены, что хотите удалить эту акцию? Информация об удалении будет записана в историю. Данные кошелька не изменятся.")) {
            const mutation = sharesType === 0 ? deleteGiftedShares : deletePurchasedShares;
            try {
                await mutation({ shareId }).unwrap();
                showNotification("Акция успешно удалена", "success");
                // Refetch the data after deletion
                if (sharesType === 0) {
                    refetchGifted();
                } else {
                    refetchPurchased();
                }
                if (onSharesUpdate) {
                    onSharesUpdate();
                }
            } catch (error) {
                console.error("Ошибка при удалении акции:", error);
                showNotification("Ошибка при удалении акции", "error");
            }
        }
    };

    const formatDateTime = (value: number): string => {
        if (!value) return "—";
        try {
            // Assuming value is a UNIX timestamp
            return new Date(value * 1000).toLocaleString("ru-RU");
        } catch {
            return String(value);
        }
    };


    if (!userId) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 transition-opacity">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div
                    className="fixed inset-0"
                    onClick={onClose}
                    aria-hidden="true"
                ></div>

                <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white text-center mb-4">
                            {sharesType === 0 ? "Подарочные акции" : "Купленные акции"} для пользователя {userData?.name} {userData?.surname} (ID: {userId})
                        </h3>

                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
                            </div>
                        ) : userSharesData.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-gray-500 dark:text-gray-400">Нет акций для отображения.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto max-h-96">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">ID Акции</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Тип</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Дата</th>
                                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Действия</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {userSharesData.map((share) => (
                                            <tr key={share.alny_shares_users_id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{share.alny_shares_users_id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    {share.alny_shares_users_type === 0 ? "Подарочная" : "Купленная"}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatDateTime(share.alny_shares_users_time)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                    <button
                                                        onClick={() => handleDeleteShare(share.alny_shares_users_id)}
                                                        disabled={isDeleting}
                                                        className="text-red-600 hover:text-red-900 dark:text-red-500 dark:hover:text-red-700 disabled:opacity-50"
                                                        title="Удалить акцию"
                                                    >
                                                        {isDeleting ? 'Удаление...' : <Trash2Icon size={18} color="red" />}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Закрыть
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
